/**
 * Material-driven order / receive field visibility.
 * Coil: thickness + width + weight (e.g. 1.2 mm · 1250 · 8 T)
 * Tube: OD × thickness × length (e.g. Ø12.40 × 1.2 × 6000 mm)
 * Sheet: thickness × width × length
 * Slit: like coil; CTL: like tube (shape from parent Tube)
 */

export type MaterialKind = "coil" | "slit" | "sheet" | "tube" | "ctl" | "other";

export type MaterialFieldRules = {
  kind: MaterialKind;
  showThickness: boolean;
  showWidth: boolean;
  /** Free OD entry — only when shape/size catalog is NOT used */
  showOd: boolean;
  showLength: boolean;
  showWtPerMm: boolean;
  /** Shape dropdown + size dropdown (dims come from catalog) */
  showShapeSize: boolean;
  requireShapeSize: boolean;
  /** Auto qty ↔ weight from OD/thk/len (tube / CTL) */
  autoTubeWeight: boolean;
  /** Prefer weight (coil) vs qty/pcs (tube) */
  preferWeight: boolean;
  shapeMaterialId: string;
  hints: {
    thickness?: string;
    width?: string;
    od?: string;
    length?: string;
    weight?: string;
    qty?: string;
  };
};

export function detectMaterialKind(name: string | null | undefined): MaterialKind {
  const n = (name ?? "").trim().toLowerCase();
  if (n === "coil" || n.includes("coil")) return "coil";
  if (n === "slit" || n.includes("slit")) return "slit";
  if (n === "sheet" || n.includes("sheet")) return "sheet";
  if (n === "tube" || n.includes("tube")) return "tube";
  if (n === "ctl" || n.includes("ctl")) return "ctl";
  return "other";
}

export function materialFieldRules(input: {
  material_id: string;
  material_name?: string | null;
  requires_shape_size?: boolean;
  parent_material_id?: string | null;
}): MaterialFieldRules {
  const kind = detectMaterialKind(input.material_name);
  const requires = Boolean(input.requires_shape_size);
  const shapeMaterialId =
    kind === "ctl" && input.parent_material_id
      ? input.parent_material_id
      : input.material_id;

  switch (kind) {
    case "coil":
    case "slit":
      return {
        kind,
        showThickness: true,
        showWidth: true,
        showOd: false,
        showLength: false,
        showWtPerMm: true,
        showShapeSize: false,
        requireShapeSize: false,
        autoTubeWeight: false,
        preferWeight: true,
        shapeMaterialId,
        hints: {
          thickness: "1.2",
          width: "1250",
          weight: "8",
          qty: "1",
        },
      };
    case "sheet":
      return {
        kind,
        showThickness: true,
        showWidth: true,
        showOd: false,
        showLength: true,
        showWtPerMm: false,
        showShapeSize: false,
        requireShapeSize: false,
        autoTubeWeight: false,
        preferWeight: true,
        shapeMaterialId,
        hints: {
          thickness: "1.2",
          width: "1250",
          length: "2500",
          weight: "2",
        },
      };
    case "tube":
      return {
        kind,
        showThickness: true,
        showWidth: false,
        showOd: false,
        showLength: true,
        showWtPerMm: false,
        showShapeSize: true,
        requireShapeSize: true,
        autoTubeWeight: true,
        preferWeight: true,
        shapeMaterialId,
        hints: {
          thickness: "1.2",
          length: "6000",
          qty: "100",
          weight: "auto",
        },
      };
    case "ctl":
      return {
        kind,
        showThickness: true,
        showWidth: false,
        showOd: false,
        showLength: true,
        showWtPerMm: false,
        showShapeSize: true,
        requireShapeSize: true,
        autoTubeWeight: true,
        preferWeight: true,
        shapeMaterialId,
        hints: {
          thickness: "1.2",
          length: "6000",
          qty: "50",
          weight: "auto",
        },
      };
    default:
      return {
        kind: "other",
        showThickness: true,
        showWidth: true,
        showOd: false,
        showLength: true,
        showWtPerMm: false,
        showShapeSize: requires,
        requireShapeSize: requires,
        autoTubeWeight: false,
        preferWeight: true,
        shapeMaterialId,
        hints: {},
      };
  }
}

/** Alias used by receive dialog */
export type ReceiveFieldRules = MaterialFieldRules;

export function receiveFieldRules(input: {
  material_id: string;
  material_name?: string | null;
  requires_shape_size?: boolean;
  parent_material_id?: string | null;
}): ReceiveFieldRules {
  return materialFieldRules(input);
}

export function calcWtPerMm(weight: number, widthMm: number): number | null {
  if (!(weight > 0) || !(widthMm > 0)) return null;
  return Math.round((weight / widthMm) * 1000 * 100) / 100;
}

/** Auto line description from ordered dims */
export function buildOrderSpecDescription(input: {
  kind: MaterialKind;
  thickness?: number | null;
  width?: number | null;
  od?: number | null;
  length?: number | null;
  weight?: number | null;
  qty?: number | null;
  shapeLabel?: string | null;
}): string {
  const thk = num(input.thickness);
  const width = num(input.width);
  const od = num(input.od);
  const len = num(input.length);
  const wt = num(input.weight);
  const qty = num(input.qty);

  if (input.kind === "coil" || input.kind === "slit") {
    const parts = [
      thk != null ? `${fmt(thk)} mm` : null,
      width != null ? `${fmt(width)} mm wide` : null,
      wt != null && wt > 0 ? `${fmt(wt)} T` : null,
    ].filter(Boolean);
    return parts.join(" · ");
  }

  if (input.kind === "tube" || input.kind === "ctl") {
    if (od != null && thk != null && len != null) {
      return `Ø${fmt(od)} × ${fmt(thk)} × ${fmt(len)} mm`;
    }
    if (input.shapeLabel && len != null) {
      return `${input.shapeLabel} × ${fmt(len)} mm`;
    }
    if (input.shapeLabel) return input.shapeLabel;
    const parts = [
      od != null ? `Ø${fmt(od)}` : null,
      thk != null ? `${fmt(thk)} mm thk` : null,
      len != null ? `${fmt(len)} mm` : null,
      qty != null && qty > 0 ? `${fmt(qty)} pcs` : null,
    ].filter(Boolean);
    return parts.join(" · ");
  }

  if (input.kind === "sheet") {
    if (thk != null && width != null && len != null) {
      return `${fmt(thk)} × ${fmt(width)} × ${fmt(len)} mm`;
    }
    return [thk, width, len]
      .filter((v) => v != null)
      .map((v) => `${fmt(v!)}`)
      .join(" × ");
  }

  return "";
}

function num(v: unknown): number | null {
  if (v === "" || v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number) {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}
